import { Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { InventoryItem, Role } from '@/lib/schemas';
import { cn } from '@/lib/utils';

interface InventoryTableProps {
  items: InventoryItem[];
  role: Role;
  onLogDelivery: (itemId: string) => void;
}

function DaysCell({ days }: { days: number | null }) {
  if (days == null) return <span className="text-ndd-500">—</span>;
  const color =
    days < 14 ? 'text-red-400 font-semibold' : days < 30 ? 'text-amber-400' : 'text-ndd-400';
  return <span className={color}>{days}d</span>;
}

export function InventoryTable({ items, role, onLogDelivery }: InventoryTableProps) {
  const isFarmer = role === 'farmer';

  return (
    <div className="rounded-lg border border-ndd-800 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Qty On Hand</TableHead>
            <TableHead>Unit</TableHead>
            <TableHead>Price/Unit</TableHead>
            <TableHead>Total Value</TableHead>
            <TableHead>Daily Usage</TableHead>
            <TableHead>Days Remaining</TableHead>
            <TableHead>Alert (days)</TableHead>
            {isFarmer && <TableHead />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={isFarmer ? 10 : 9} className="py-8 text-center text-ndd-400">
                No inventory items found
              </TableCell>
            </TableRow>
          )}
          {items.map((item) => (
            <TableRow
              key={item.item_id}
              className={cn(item.below_threshold && 'border-l-2 border-l-red-500')}
            >
              <TableCell>
                <span className="flex items-center gap-2 font-medium">
                  {item.below_threshold && <Package size={14} className="text-red-400" />}
                  {item.name}
                </span>
              </TableCell>
              <TableCell className="capitalize text-ndd-400 text-sm">{item.category}</TableCell>
              <TableCell className="font-mono text-sm">
                {item.quantity_on_hand.toLocaleString()}
              </TableCell>
              <TableCell className="text-sm text-ndd-400">{item.unit}</TableCell>
              <TableCell className="text-sm">
                {item.price_per_unit != null ? `$${item.price_per_unit.toFixed(2)}` : '—'}
              </TableCell>
              <TableCell className="text-sm">
                {item.total_value != null
                  ? `$${item.total_value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                  : '—'}
              </TableCell>
              <TableCell className="text-sm text-ndd-400">
                {item.daily_usage_rate != null
                  ? `${item.daily_usage_rate.toFixed(0)} lbs/day`
                  : '—'}
              </TableCell>
              <TableCell>
                <DaysCell days={item.days_remaining} />
              </TableCell>
              <TableCell className="text-sm text-ndd-400">{item.alert_threshold_days}d</TableCell>
              {isFarmer && (
                <TableCell>
                  <Button variant="outline" size="sm" onClick={() => onLogDelivery(item.item_id)}>
                    Log Delivery
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
